import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    baseUrl: string;
    extraParams?: Record<string, string | number>;
}

export default function Pagination({ currentPage, totalPages, baseUrl, extraParams }: PaginationProps) {
    const [searchParams] = useSearchParams();

    if (totalPages <= 1) return null;

    const createPageUrl = (page: number) => {
        const params = new URLSearchParams(searchParams.toString());

        // Add extra params if provided
        if (extraParams) {
            Object.entries(extraParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.set(key, value.toString());
                }
            });
        }

        params.set('pagina', page.toString());

        // Check if baseUrl already has query params
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}${params.toString()}`;
    };

    const pages = [];
    const maxVisiblePages = 3;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <nav
            className="mt-8 w-full flex justify-center"
            aria-label="Paginação"
        >
            <div className="inline-flex items-center gap-1.5 md:gap-2 p-1.5 md:p-2">
            {/* Botão Anterior */}
            <Link
                to={createPageUrl(currentPage - 1)}
                className={`h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-xl border transition-all duration-200 ${
                    currentPage === 1
                    ? 'pointer-events-none border-white/10 bg-white/[0.02] text-white/25'
                    : 'border-white/10 bg-white/[0.02] text-white/80 hover:text-white hover:bg-white/10 hover:border-orange-500/40'
                    }`}
                aria-disabled={currentPage === 1}
                aria-label="Página anterior"
            >
                <ChevronLeft size={18} />
            </Link>

            {/* Páginas */}
            <div className="flex items-center gap-1.5 md:gap-2">
                {startPage > 1 && (
                    <>
                        <Link
                            to={createPageUrl(1)}
                            className="h-9 min-w-9 px-2 md:h-10 md:min-w-10 md:px-3 flex items-center justify-center rounded-xl bg-white/[0.02] border border-white/10 text-white/85 hover:text-white hover:bg-white/10 hover:border-orange-500/40 transition-all font-semibold text-sm"
                        >
                            1
                        </Link>
                        {startPage > 2 && <span className="text-white/30 text-xs md:text-sm px-0.5">...</span>}
                    </>
                )}

                {pages.map((page) => (
                    <Link
                        key={page}
                        to={createPageUrl(page)}
                        className={`h-9 min-w-9 px-2 md:h-10 md:min-w-10 md:px-3 flex items-center justify-center rounded-xl border transition-all duration-200 font-bold text-sm ${
                            currentPage === page
                            ? 'bg-orange-500/90 text-white border-orange-400 shadow-[0_0_0_1px_rgba(251,146,60,0.18),0_6px_18px_-8px_rgba(249,115,22,0.7)]'
                            : 'bg-white/[0.02] border-white/10 text-white/85 hover:text-white hover:bg-white/10 hover:border-orange-500/40'
                            }`}
                        aria-current={currentPage === page ? 'page' : undefined}
                    >
                        {page}
                    </Link>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="text-white/30 text-xs md:text-sm px-0.5">...</span>}
                        <Link
                            to={createPageUrl(totalPages)}
                            className="h-9 min-w-9 px-2 md:h-10 md:min-w-10 md:px-3 flex items-center justify-center rounded-xl bg-white/[0.02] border border-white/10 text-white/85 hover:text-white hover:bg-white/10 hover:border-orange-500/40 transition-all font-semibold text-sm"
                        >
                            {totalPages}
                        </Link>
                    </>
                )}
            </div>

            {/* Botão Próximo */}
            <Link
                to={createPageUrl(currentPage + 1)}
                className={`h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-xl border transition-all duration-200 ${
                    currentPage === totalPages
                    ? 'pointer-events-none border-white/10 bg-white/[0.02] text-white/25'
                    : 'border-white/10 bg-white/[0.02] text-white/80 hover:text-white hover:bg-white/10 hover:border-orange-500/40'
                    }`}
                aria-disabled={currentPage === totalPages}
                aria-label="Próxima página"
            >
                <ChevronRight size={18} />
            </Link>
            </div>
        </nav>
    );
}
